import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";
import { specials } from "@/mocks/specials";

const typeTabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "影片" },
  { key: "special", label: "专题" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const type = searchParams.get("type") ?? "all";
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  usePageMeta({
    title: query ? `“${query}” 搜索结果 - 555电影` : "站内搜索 - 555电影",
    description: query
      ? `555电影搜索“${query}”的结果，为你匹配相关影片与专题片单。`
      : "在 555电影站内搜索电影、专题片单，快速找到想看的内容。",
    keywords: "电影搜索,站内搜索,555电影",
  });

  const matchedMovies = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return [];
    return movies.filter((m) => {
      const text = [m.title, m.originalTitle, m.region, m.director, ...m.genres, ...m.cast]
        .join(" ")
        .toLowerCase();
      return text.includes(kw);
    });
  }, [query]);

  const matchedSpecials = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return [];
    return specials.filter((s) =>
      [s.title, s.subtitle, s.description].join(" ").toLowerCase().includes(kw),
    );
  }, [query]);

  const showMovies = type === "all" || type === "movie";
  const showSpecials = type === "all" || type === "special";
  const totalCount =
    (showMovies ? matchedMovies.length : 0) + (showSpecials ? matchedSpecials.length : 0);

  const hotPicks = [...movies].sort((a, b) => b.hot - a.hot).slice(0, 4);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    const kw = input.trim();
    if (kw) params.set("q", kw);
    else params.delete("q");
    setSearchParams(params);
  };

  const setType = (t: string) => {
    const params = new URLSearchParams(searchParams);
    if (t === "all") params.delete("type");
    else params.set("type", t);
    setSearchParams(params);
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div className="w-full animate-fadeIn">
      <div className="w-full bg-primary-950">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-8 md:px-6 md:py-12">
          <div className="mb-4 text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "站内搜索" }]} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            站内搜索
          </h1>
          <form onSubmit={submit} className="mt-5 max-w-2xl">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  autoFocus
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入电影名、类型、导演或专题关键词"
                  className="h-12 w-full rounded-md border border-background-50/20 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
                />
              </div>
              <button
                type="submit"
                className="h-12 shrink-0 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-screen-xl px-4 py-8 md:px-6 md:py-10">
        {hasQuery ? (
          <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                关键词 <span className="font-semibold text-primary-600">“{query}”</span> 共匹配到{" "}
                <span className="font-semibold text-primary-600">{totalCount}</span> 条结果
              </p>
              <div className="flex rounded-full border border-background-200 bg-background-100 p-1">
                {typeTabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setType(t.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-1 text-xs font-medium transition-colors ${
                      type === t.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {totalCount === 0 ? (
              <div>
                <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                  <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <p className="text-base font-medium text-foreground-800">
                    没有找到与“{query}”相关的内容
                  </p>
                  <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
                </div>
                <div className="mt-10">
                  <div className="mb-5 flex items-center gap-2">
                    <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                    <h2 className="font-heading text-xl font-bold text-foreground-950">
                      热门推荐
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                    {hotPicks.map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-10">
                {showMovies && matchedMovies.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                        <i className="ri-film-line text-base"></i>
                      </span>
                      影片（{matchedMovies.length}）
                    </h2>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                      {matchedMovies.map((m) => (
                        <MovieCard key={m.id} movie={m} />
                      ))}
                    </div>
                  </section>
                )}

                {showSpecials && matchedSpecials.length > 0 && (
                  <section>
                    <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="flex h-5 w-5 items-center justify-center text-secondary-500">
                        <i className="ri-bookmark-3-line text-base"></i>
                      </span>
                      专题（{matchedSpecials.length}）
                    </h2>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {matchedSpecials.map((s) => (
                        <Link
                          key={s.id}
                          to={`/specials?focus=${s.id}`}
                          className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                        >
                          <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                            <img
                              src={s.cover}
                              alt={`${s.title} 封面`}
                              title={`${s.title} - 555电影`}
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                          <div className="min-w-0">
                            <h3 className="line-clamp-1 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                              {s.title}
                            </h3>
                            <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
                              {s.subtitle}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-search-2-line text-2xl"></i>
            </span>
            <p className="text-base font-medium text-foreground-800">输入关键词开始搜索</p>
            <p className="mt-1 text-sm text-foreground-500">
              试试搜索「科幻」「星海彼岸」或「悬疑专题」
            </p>
          </div>
        )}
      </div>
    </div>
  );
}